import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import TokenStatus from './components/TokenStatus';
import ApiTestPage from './components/ApiTestPage';
import './App.css';

// Main App component with authentication context
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

// App content component that uses authentication context
function AppContent() {
  const [currentPage, setCurrentPage] = useState('profile');
  const {
    isAuthenticated,
    isLoading,
    userProfile,
    error,
    login: handleLogin,
    logout: handleLogout,
    clearError
  } = useAuth();

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="container">
        <div className="loading">
          <h2>Loading...</h2>
          <p>Checking authentication status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={clearError} className="login-button">
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container">
        <div className="login-container">
          <h1>Azure AD SSO Demo</h1>
          <p>Welcome! Please sign in with your Microsoft account to continue.</p>
          <button onClick={handleLogin} className="login-button">
            Login with Microsoft
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <TokenStatus showDetails={true} />
      
      <ProtectedRoute>
        <div className="navigation">
          <button 
            className={`nav-button ${currentPage === 'profile' ? 'active' : ''}`}
            onClick={() => setCurrentPage('profile')}
          >
            Profile
          </button>
          <button 
            className={`nav-button ${currentPage === 'api-test' ? 'active' : ''}`}
            onClick={() => setCurrentPage('api-test')}
          >
            API Test
          </button>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>

        {currentPage === 'profile' && (
          <div className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                {getInitials(userProfile?.name)}
              </div>
              <div className="profile-info">
                <h2>Welcome, {userProfile?.name || 'User'}</h2>
                <p>{userProfile?.email || 'No email available'}</p>
              </div>
            </div>

            <div className="profile-details">
              <div className="detail-item">
                <div className="detail-label">Name</div>
                <div className="detail-value">{userProfile?.name || 'N/A'}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">Email</div>
                <div className="detail-value">{userProfile?.email || 'N/A'}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">Department</div>
                <div className="detail-value">{userProfile?.department || 'N/A'}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">User ID</div>
                <div className="detail-value">{userProfile?.user_id || 'N/A'}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">Tenant ID</div>
                <div className="detail-value">{userProfile?.tenant_id || 'N/A'}</div>
              </div>

              <div className="detail-item">
                <div className="detail-label">UPN</div>
                <div className="detail-value">{userProfile?.upn || 'N/A'}</div>
              </div>

              {userProfile?.roles && userProfile.roles.length > 0 && (
                <div className="detail-item">
                  <div className="detail-label">Roles</div>
                  <div className="detail-value">
                    {userProfile.roles.join(', ')}
                  </div>
                </div>
              )}

              {userProfile?.groups && userProfile.groups.length > 0 && (
                <div className="detail-item">
                  <div className="detail-label">Groups</div>
                  <div className="detail-value">
                    {userProfile.groups.join(', ')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {currentPage === 'api-test' && <ApiTestPage />}
      </ProtectedRoute>
    </div>
  );
}

export default App;

